import React, { useState } from 'react'

import { faEllipsis } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Button,
  Divider,
  FormControl,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Toolbar,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import PropTypes from 'prop-types'

import ErrorMessage from '../../../../components/ErrorMessage'
import TableHeader from './Header'
import TableRowLoading from './RowLoading'
import { rowHeight } from './constants'

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1
  }
  if (b[orderBy] > a[orderBy]) {
    return 1
  }
  return 0
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index])
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) return order
    return a[1] - b[1]
  })
  return stabilizedThis.map((el) => el[0])
}

const useStyles = makeStyles((theme) => ({
  paper: {
    width: '100%',
  },
  toolbar: {
    padding: theme.spacing(2),
  },
  title: {
    flex: '1 1 100%',
  },
  formControl: {
    minWidth: 200,
  },
  rowStyles: {
    '& .MuiTableCell-root:last-child': {
      paddingRight: '24px',
    },
  },
  headerCell: {
    minWidth: 200,
  },
}))

const DashTable = ({
  actionTooltip,
  actions,
  columns,
  defaultFilter,
  error,
  groups,
  loading,
  onFilterChange,
  tableName,
}) => {
  const classes = useStyles()

  const [order, setOrder] = useState('desc')
  const [orderBy, setOrderBy] = useState('count')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(3)
  const [filter, setFilter] = useState(defaultFilter)
  const [anchorEl, setAnchorEl] = useState(null)
  const [openMenuId, setOpenMenuId] = useState(null)

  const dashRowHeight = rowHeight / 2

  const handleRequestSort = (_event, property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
    setPage(0)
  }

  const handleFilterChange = (event) => {
    setFilter(event.target.value)
    setPage(0)
    onFilterChange?.(event.target.value)
  }

  const getRows = (selectedFilter) => {
    if (loading) {
      return []
    }

    const group = groups.find((g) => g.id === selectedFilter)
    return group ? group.rows : []
  }

  const handleChangePage = (_event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleMenuOpen = (event, menuId) => {
    setAnchorEl(event.currentTarget)
    setOpenMenuId(menuId)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setOpenMenuId(null)
  }

  const emptyRows = rowsPerPage - Math.min(rowsPerPage, getRows(filter).length - page * rowsPerPage)

  const getLoadingState = () => (
    <React.Fragment>
      {[...Array(rowsPerPage).keys()].map((index) => (
        <TableRowLoading
          key={index}
          index={index}
          columnCount={columns.length}
        />
      ))}
    </React.Fragment>
  )

  const getErrorState = () => (
    <TableRow style={{ height: dashRowHeight * rowsPerPage }}>
      <TableCell colSpan={columns.length + 1}>
        <ErrorMessage title="Sorry, we don’t currently have any data for this selection." />
      </TableCell>
    </TableRow>
  )

  const renderTableBody = () => (
    <React.Fragment>
      {stableSort(getRows(filter), getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((row) => {
          const actionButtonId = `action-button-${row.name}`
          const actionMenuId = `action-menu-${row.name}`
          const open = openMenuId === actionMenuId

          return (
            <TableRow
              key={row.name}
              className={classes.rowStyles}
            >
              {columns &&
                columns.map((column) => (
                  <TableCell
                    className={column.header ? classes.headerCell : undefined}
                    component={column.header ? 'th' : 'td'}
                    scope={column.header ? 'row' : ''}
                    key={column.id}
                    align={column.align || 'left'}
                  >
                    {column.format?.(row[column.id]) || row[column.id]}
                  </TableCell>
                ))}
              <TableCell
                align="right"
                className={classes.rowStyles}
              >
                <Button
                  id={actionButtonId}
                  aria-controls={open ? actionMenuId : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                  aria-label={actionButtonId}
                  onClick={(event) => handleMenuOpen(event, actionMenuId)}
                  style={{ justifyContent: 'flex-end' }}
                >
                  <FontAwesomeIcon icon={faEllipsis} />
                </Button>
                <Menu
                  id={actionMenuId}
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleMenuClose}
                  MenuListProps={{
                    'aria-labelledby': actionButtonId,
                  }}
                >
                  {actions &&
                    actions.map((action) => (
                      <MenuItem
                        key={action.label}
                        onClick={() => {
                          handleMenuClose()
                          //  Executing this after the render has completed
                          //  due to both the <Menu /> and the <Modal /> components
                          //  futzing with the body's style and attempting to restore it to the
                          //  original state.  Without the setTimeout, the Menu component will remove
                          //  the page's scrollbar by setting `overflow: none`, then clicking on an
                          //  action that opens a modal will capture the body's style (including the
                          //  `overflow: none`, do its thing, then reset the body's stil to its
                          //  "original" state (ie: with no scrollbar because the menu was open)
                          setTimeout(() => action.onClick(row), 0)
                        }}
                      >
                        {action.label}
                      </MenuItem>
                    ))}
                </Menu>
              </TableCell>
            </TableRow>
          )
        })}
      {emptyRows > 0 && (
        <TableRow style={{ height: dashRowHeight * emptyRows }}>
          <TableCell colSpan={6} />
        </TableRow>
      )}
    </React.Fragment>
  )
  return (
    <Paper
      className={classes.paper}
      elevation={0}
    >
      <Toolbar className={classes.toolbar}>
        <Typography
          className={classes.title}
          variant="body1"
          color="textPrimary"
        >
          {tableName}
        </Typography>
        <FormControl
          className={classes.formControl}
          variant="outlined"
          size="small"
        >
          <Select
            labelId="reach-table-filter-label"
            id="reach-table-filter"
            value={filter}
            onChange={handleFilterChange}
            disabled={loading || error}
          >
            {groups &&
              groups.map((group) => (
                <MenuItem
                  key={group.id}
                  value={group.id}
                >
                  {group.label}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Toolbar>
      <Divider light />
      <TableContainer>
        <Table
          size={'medium'}
          aria-label={'reach table'}
          aria-labelledby={'reach'}
        >
          <TableHeader
            columns={[
              ...columns,
              {
                id: 'action',
                label: 'Actions',
                enableSort: false,
                align: 'right',
                tooltip: actionTooltip,
                headerStyle: classes.rowStyles,
              },
            ]}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />

          <TableBody>
            {loading ? getLoadingState() : error || getRows(filter).length === 0 ? getErrorState() : renderTableBody()}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[rowsPerPage]}
        component="div"
        count={getRows(filter).length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  )
}

DashTable.propTypes = {
  actionTooltip: PropTypes.string,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
    }).isRequired,
  ).isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      enableSort: PropTypes.bool,
      align: PropTypes.string,
      format: PropTypes.func,
      header: PropTypes.bool,
    }),
  ).isRequired,
  defaultFilter: PropTypes.string.isRequired,
  error: PropTypes.bool.isRequired,
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      rows: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          count: PropTypes.number.isRequired,
        }).isRequired,
      ).isRequired,
    }).isRequired,
  ),
  loading: PropTypes.bool.isRequired,
  onFilterChange: PropTypes.func,
  tableName: PropTypes.string.isRequired,
}

export default DashTable
